import React, { useCallback } from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis, Tooltip } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, ArrowRight, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';

const formatWeekday = (weekday) => {
  const day = { Mo: 1, Tu: 2, We: 3, Th: 4, Fr: 5, Sa: 6, Su: 0 }[weekday] ?? 0;
  return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date(2024, 0, day));
};

const formatValue = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const CHANGE_ICON = {
  positive: TrendingUp,
  neutral: ArrowRight,
  negative: TrendingDown,
};

const CHANGE_VARIANT = {
  positive: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800',
  neutral: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
  negative: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
};

export function KPIBarCard({ title, value, change, changeType = 'positive', chartData, index = 0, className }) {
  const ChangeIcon = CHANGE_ICON[changeType] || TrendingUp;

  const handleMouseEnter = useCallback((itemIndex) => {
    const bars = document.querySelectorAll(`#kpi-chart-${index} .recharts-bar-rectangle`);
    bars.forEach((bar, i) => {
      if (i !== itemIndex) {
        const path = bar.querySelector('path');
        if (path) path.setAttribute('fill', 'hsl(38, 22%, 88%)');
      }
    });
  }, [index]);

  const handleMouseLeave = useCallback(() => {
    const bars = document.querySelectorAll(`#kpi-chart-${index} .recharts-bar-rectangle`);
    bars.forEach((bar) => {
      const path = bar.querySelector('path');
      if (path) path.setAttribute('fill', 'hsl(210, 55%, 40%)');
    });
  }, [index]);

  return (
    <Card className={cn('border-[#E7E0D6] dark:border-white/[0.06] bg-white dark:bg-[#1E2025] min-h-[120px]', className)}>
      <CardContent className="p-4">
        <section className="flex h-full flex-nowrap items-center justify-between">
          <div className="flex h-full flex-col justify-between gap-y-3">
            <dt className="text-sm font-medium text-[#5B616E] dark:text-white/60 flex items-center gap-x-2">
              {title}
              <span className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium', CHANGE_VARIANT[changeType])}>
                <ChangeIcon className="h-3 w-3" />
                {change}
              </span>
            </dt>
            <dd className="text-3xl font-semibold text-[#121316] dark:text-white/90">{value}</dd>
          </div>
          <div className="flex h-[120px] w-[180px] shrink-0 items-center">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                accessibilityLayer
                barSize={12}
                data={chartData}
                id={`kpi-chart-${index}`}
                margin={{ top: 24, bottom: 4 }}
              >
                <XAxis
                  axisLine={false}
                  dataKey="weekday"
                  style={{ fontSize: '11px' }}
                  tickLine={false}
                />
                <Tooltip
                  content={({ label, payload }) => (
                    <div className="flex h-8 min-w-[80px] items-center gap-x-2 rounded-lg bg-white p-2 text-xs shadow-md border border-[#E7E0D6]">
                      <span className="h-2 w-2 rounded-full bg-[#1E5A8A]" />
                      <span className="text-[#5B616E]">{formatWeekday(label)}</span>
                      <span className="font-medium text-[#121316]">
                        {formatValue(payload?.[0]?.value)}
                      </span>
                    </div>
                  )}
                  cursor={false}
                />
                <Bar
                  background={{ fill: 'hsl(38, 22%, 92%)', radius: 8 }}
                  className="transition-colors"
                  dataKey="value"
                  fill="hsl(210, 55%, 40%)"
                  radius={8}
                  onMouseEnter={(_, itemIndex) => handleMouseEnter(itemIndex)}
                  onMouseLeave={handleMouseLeave}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}

export function KPIBarGrid({ items }) {
  return (
    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item, index) => (
        <KPIBarCard key={index} index={index} {...item} />
      ))}
    </div>
  );
}
